import React, { memo, useMemo, useState } from 'react'
import { Col, Button, Table, Empty, Input, Space, theme, Row, message, Tooltip, Skeleton, Typography } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faRefresh,
  faSearch,
  faUserPlus,
  faFileAlt,
  faMessage,
  faUsers,
  faUserDoctor,
  faUserInjured,
  faEdit,
  faStethoscope
} from '@fortawesome/free-solid-svg-icons'
import _ from 'lodash'
import PropTypes from 'prop-types'

import { ContainerCard } from '../../../reusable-components/AgiliteCards'
import TeamChatForm from '../../../TeamChat/components/team-chat-form'
import coreReducer from '../../../../core/utils/reducer'
import SystemUsersTemplates from '../utils/templates'
import useSystemUsersTable from '../hooks/useSystemUsersTable'
import { getInviteId, getInviteLabel } from '../utils/constants'
import dayjs from 'dayjs'
import { useSelector } from 'react-redux'
import { readBookings } from '../../../Bookings/utils/utils'
import PatientExaminationForm from '../../../Bookings/components/ExaminationWrapper'
import { getBookingState } from '../../../Bookings/utils/bpm-utils'
import CoreEnums from '../../../../core/utils/enums'
import PageHeader from '../../../reusable-components/PageHeader'
import BeginExaminationModal from './BeginExaminationModal.js'
import { isMedicalProfessional } from '../../../lib/profile-utils'

// Add spin animation with more refined keyframes
const spinAnimation = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    50% { transform: rotate(180deg); }
    100% { transform: rotate(360deg); }
  }
  
  .ant-table-row:hover .service-icon {
    transform: scale(1.1);
    transition: all 0.2s ease;
  }
  
  .service-icon {
    transition: all 0.2s ease;
  }
  
  .ant-table-row {
    transition: all 0.3s ease;
  }
  
  .ant-table-row:hover {
    background-color: rgba(24, 144, 255, 0.05) !important;
  }

  .ant-table-thead th h2 {
    font-size: 15px !important;
    margin: 0;
  }
`

const SystemUsersTable = ({
  addPatient,
  createRecord,
  editRecord,
  loading,
  setLoading,
  type,
  handleSearch,
  clinics,
  page,
  pageSize,
  refreshView,
  setPage,
  setPageSize,
  total
}) => {
  const authState = useSelector((state) => state.auth)
  const servicesState = useSelector((state) => state.services.data)
  const clinicsState = useSelector((state) => state.clinics.data)
  const [consultLoading, setConsultLoading] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState(null)
  const { token } = theme.useToken()
  // Standard action color to use throughout
  const ACTION_COLOR = token.actionColor

  // Button styling to match FeatureButtonsRow
  const buttonStyle = {
    height: '40px',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '0 16px',
    fontWeight: 500,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s ease'
  }

  // Check if the current user is a medical professional
  const userIsMedicalProfessional = useMemo(() => {
    return isMedicalProfessional()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState.agiliteUser.extraData.role.type])

  const {
    viewType,
    tableData,
    userFilters,
    teamChatProps,
    setTeamChatProps,
    state,
    coreState,
    dispatch,
    beginAdhocScreening,
    setExaminationModalOpen,
    examinationModalOpen
  } = useSystemUsersTable({ type, clinics })

  const handleCreateRecord = () => {
    createRecord()
    if (coreState.tourOpen) {
      dispatch(coreReducer.actions.nextTourStep())
    }
  }

  const handleStartConsult = async (record) => {
    if (!record || !record._id) return

    try {
      // Show loading state
      setConsultLoading(true)

      // Get the patient's last appointment
      const patientAppointments = await readBookings({ userRef: record._id })

      if (!patientAppointments || patientAppointments.length === 0) {
        message.info('No appointments found for this patient')
        setConsultLoading(false)
        return
      }

      // Sort appointments by date (newest first)
      const sortedAppointments = patientAppointments.sort((a, b) => {
        const dateA = new Date(a.bookingDate)
        const dateB = new Date(b.bookingDate)
        return dateB - dateA
      })

      const lastAppointment = sortedAppointments[0]

      // Get workflow history if available
      let workflowHistory = {}
      if (lastAppointment.processRef) {
        workflowHistory = await getBookingState([lastAppointment.processRef])
        workflowHistory.history.splice(0, 1)
      }

      // Create a new data object that includes the patient record
      const appointmentData = {
        ...lastAppointment,
        patientRecord: {
          ...record,
          medicalAid: record.medicalAid || {} // Ensure medicalAid exists
        },
        mainMemberProfile: {
          ...record,
          medicalAid: record.medicalAid || {} // Ensure medicalAid exists
        }
      }

      // Open the appointment in a new tab
      dispatch(
        coreReducer.actions.addTab({
          key: `${CoreEnums.tabKeys.MY_BOOKINGS}_${lastAppointment._id}`,
          closable: true,
          label: `Booking: ${record.firstName} ${record.lastName}`,
          children: (
            <PatientExaminationForm data={appointmentData} refreshView={() => {}} workflowHistory={workflowHistory} />
          )
        })
      )
    } catch (error) {
      message.error('Failed to open patient consultation: ' + error.message)
    } finally {
      setConsultLoading(false)
    }
  }

  const getSearchPlaceholder = () => {
    if (type === 'system' || type === 'medical') {
      return 'Search for First Name, Last Name or Email...'
    }
    return 'Search for Name, ID/Passport, Email, Address...'
  }

  // Get the appropriate title, description and icon based on the user type
  const getTitleInfo = () => {
    switch (type) {
      case 'patient':
        return {
          title: 'Patient Management',
          description: 'Manage patients, view profiles, and access medical records.',
          icon: faUserInjured
        }
      case 'medical':
        return {
          title: 'Medical Professionals',
          description: 'Manage all healthcare professionals, their specialties and clinic assignments.',
          icon: faUserDoctor
        }
      case 'system':
      default:
        return {
          title: 'System Users',
          description: 'Manage staff accounts, permissions, and system access.',
          icon: faUsers
        }
    }
  }

  const titleInfo = getTitleInfo()

  const renderAddButton = () => {
    if (type === 'patient') {
      return (
        <Button
          type='primary'
          style={{
            background: loading ? undefined : token.actionColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            height: '40px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
            borderRadius: '6px'
          }}
          icon={<FontAwesomeIcon icon={faUserPlus} />}
          onClick={addPatient}
          disabled={loading}
          size='large'
        >
          Add Patient
        </Button>
      )
    }

    const allowedRoles = ['admin', 'reception', 'super_admin']
    const userRole = state.auth.agiliteUser?.extraData?.role?.type

    if (allowedRoles.includes(userRole)) {
      return (
        <Button
          type='primary'
          style={{
            background: loading ? undefined : token.actionColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            height: '40px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
            borderRadius: '6px'
          }}
          icon={<FontAwesomeIcon icon={faUserPlus} />}
          onClick={handleCreateRecord}
          disabled={loading}
          size='large'
          id={getInviteId(type)}
        >
          Add {getInviteLabel(type)}
        </Button>
      )
    }

    return null
  }

  const getServices = (services) => {
    if (!services) return 'N/A'
    return services.map((service) => servicesState.find((s) => s._id === service)?.name).join(', ')
  }

  const getClinics = (clinics) => {
    if (!clinics) return 'N/A'
    return clinics.map((clinic) => clinicsState.find((c) => c._id === clinic)?.name).join(', ')
  }

  // Function to generate skeleton rows based on current pageSize
  const generateSkeletonRows = () => {
    const rows = []
    const approxColumnCount = 6 // Approximate number of columns in the table

    for (let i = 0; i < pageSize; i++) {
      rows.push({
        key: `skeleton-${i}`,
        _id: `skeleton-${i}`,
        skeleton: true
      })
    }

    return rows
  }

  // Function to generate a skeleton expanded row
  const renderSkeletonExpandedRow = () => (
    <Row style={{ background: 'white', padding: 8 }}>
      <Col span={12}>
        <div>
          <Space direction='vertical' style={{ width: '100%' }}>
            <Space>
              <p style={{ width: 80 }}>
                <b>Status:</b>
              </p>
              <Skeleton.Input active size='small' style={{ width: 80 }} />
            </Space>
            <Space>
              <p style={{ width: 80 }}>
                <b>Manager:</b>
              </p>
              <Skeleton.Input active size='small' style={{ width: 80 }} />
            </Space>
            <Space>
              <p style={{ width: 80 }}>
                <b>Address:</b>
              </p>
              <Skeleton.Input active size='small' style={{ width: 120 }} />
            </Space>
            <Space>
              <p style={{ width: 80 }}>
                <b>DOB:</b>
              </p>
              <Skeleton.Input active size='small' style={{ width: 100 }} />
            </Space>
          </Space>
        </div>
      </Col>
      <Col span={12}>
        <div>
          <Space direction='vertical' style={{ width: '100%' }}>
            <Space>
              <p style={{ width: 110 }}>
                <b>Profession:</b>
              </p>
              <Skeleton.Input active size='small' style={{ width: 120 }} />
            </Space>
            <Space>
              <p style={{ width: 110 }}>
                <b>Services:</b>
              </p>
              <Skeleton.Input active size='small' style={{ width: 150 }} />
            </Space>
            <Space>
              <p style={{ width: 110 }}>
                <b>Clinics:</b>
              </p>
              <Skeleton.Input active size='small' style={{ width: 150 }} />
            </Space>
          </Space>
        </div>
      </Col>
    </Row>
  )

  // Function to generate skeleton columns
  const skeletonColumns = () => {
    // Get the columns from the template
    let columns = SystemUsersTemplates.columnTemplate(
      userFilters,
      type,
      clinics,
      token,
      setTeamChatProps,
      editRecord,
      handleStartConsult,
      false,
      consultLoading
    )
      .filter((i) => !i.hidden)
      .map((col) => ({
        ...col,
        render: () => <Skeleton.Input active size='small' style={{ width: '100%' }} />,
        onCell: () => ({
          style: { cursor: 'default' }
        })
      }))

    // Add actions column at the end (fixed to right)
    columns.push({
      title: <h2>Actions</h2>,
      key: 'actions',
      align: 'center',
      fixed: 'right',
      width: 100,
      render: () => <Skeleton.Input active size='small' style={{ width: '100%' }} />
    })

    return columns
  }

  return (
    <>
      <style>{spinAnimation}</style>
      <ContainerCard bodyStyle={{ padding: 0 }}>
        <PageHeader title={titleInfo.title} icon={titleInfo.icon} />

        <div style={{ padding: '16px 24px' }}>
          <Row gutter={[12, 16]}>
            <Col span={24}>
              <Row gutter={[0, 0]} align='middle' justify='space-between' wrap>
                <Col xs={24} sm={14} md={16} lg={16} xl={16}>
                  <Input
                    style={{
                      width: '425px',
                      maxWidth: '100%',
                      borderRadius: '6px',
                      height: '40px'
                    }}
                    placeholder={getSearchPlaceholder()}
                    prefix={
                      <span
                        style={{
                          background: token.actionColor,
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '8px'
                        }}
                      >
                        <FontAwesomeIcon icon={faSearch} style={{ color: 'white', fontSize: '12px' }} />
                      </span>
                    }
                    allowClear
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                </Col>
                <Col xs={24} sm={10} md={8} lg={8} xl={8}>
                  <Row gutter={[12, 8]} justify='end'>
                    <Col>
                      <Tooltip title='Refresh list'>
                        <Button
                          icon={
                            <FontAwesomeIcon
                              icon={faRefresh}
                              style={{
                                color: ACTION_COLOR,
                                animation: loading ? 'spin 1s linear infinite' : 'none',
                                marginRight: 8
                              }}
                            />
                          }
                          onClick={refreshView}
                          disabled={loading}
                          type='default'
                          size='large'
                          style={{
                            ...buttonStyle,
                            background: 'rgba(24, 144, 255, 0.1)',
                            borderColor: 'rgba(24, 144, 255, 0.3)',
                            color: ACTION_COLOR
                          }}
                        >
                          Refresh
                        </Button>
                      </Tooltip>
                    </Col>
                    <Col>{renderAddButton()}</Col>
                  </Row>
                </Col>
              </Row>
            </Col>

            <Col span={24}>
              <Table
                // className={loading ? 'custom-loading-mask' : ''}
                size='small'
                rowKey='_id'
                dataSource={loading ? generateSkeletonRows() : _.sortBy(tableData, ['firstName', 'lastName'])}
                pagination={{
                  current: page,
                  pageSize,
                  total,
                  onChange: (p, ps) => {
                    setPage(p)
                    setPageSize(ps)
                  },
                  showSizeChanger: true,
                  hideOnSinglePage: true,
                  position: ['bottomCenter'],
                  pageSizeOptions: ['10', '20', '50', '100'],
                  showTotal: (total) => `Total ${total} ${viewType}`
                }}
                columns={
                  loading
                    ? skeletonColumns()
                    : [
                        ...SystemUsersTemplates.columnTemplate(
                          userFilters,
                          type,
                          clinics,
                          token,
                          setTeamChatProps,
                          editRecord,
                          handleStartConsult,
                          false,
                          consultLoading
                        ).filter((i) => !i.hidden && i.key !== 'actions'),
                        {
                          title: <h2>Actions</h2>,
                          key: 'actions',
                          align: 'center',
                          fixed: 'right',
                          width: 150,
                          render: (_, record) => (
                            <Space size={8}>
                              <Tooltip
                                title={record.extraData?.role?.type === 'patient' ? 'View Patient Record' : 'Edit'}
                              >
                                <Button
                                  type='primary'
                                  size='small'
                                  onClick={() => editRecord(record)}
                                  icon={
                                    <FontAwesomeIcon
                                      icon={record.extraData?.role?.type === 'patient' ? faFileAlt : faEdit}
                                    />
                                  }
                                  style={{
                                    height: '32px',
                                    width: '32px',
                                    minWidth: '32px',
                                    background: token.actionColor,
                                    borderRadius: '50%',
                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                                    border: 'none',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: 0
                                  }}
                                />
                              </Tooltip>
                              <Button
                                type='primary'
                                size='small'
                                onClick={() => setTeamChatProps({ userRef: record._id, isOpen: true })}
                                icon={<FontAwesomeIcon icon={faMessage} />}
                                style={{
                                  height: '32px',
                                  width: '32px',
                                  minWidth: '32px',
                                  background: token.actionColor,
                                  borderRadius: '50%',
                                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                                  border: 'none',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: 0
                                }}
                              />
                              {userIsMedicalProfessional && (
                                <Tooltip title='Start A Consult'>
                                  <Button
                                    type='primary'
                                    size='small'
                                    onClick={() => {
                                      setSelectedRecord(record)
                                      setExaminationModalOpen(true)
                                    }}
                                    icon={<FontAwesomeIcon icon={faStethoscope} />}
                                    style={{
                                      height: '32px',
                                      width: '32px',
                                      minWidth: '32px',
                                      background: token.actionColor,
                                      borderRadius: '50%',
                                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                                      border: 'none',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      padding: 0
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </Space>
                          )
                        }
                      ]
                }
                expandable={
                  loading
                    ? {
                        // Show skeleton expanded row but disable interaction
                        expandedRowRender: renderSkeletonExpandedRow,
                        expandRowByClick: false,
                        expandIcon: ({ expanded }) => (
                          <Button type='text' size='small' disabled={true} style={{ cursor: 'default' }}>
                            {expanded ? '-' : '+'}
                          </Button>
                        )
                      }
                    : {
                        expandedRowRender: (record) => {
                          return (
                            <Row style={{ background: 'white', padding: 8 }}>
                              <Col span={12}>
                                <div>
                                  <Space direction='vertical' style={{ width: '100%' }}>
                                    <Space>
                                      <p style={{ width: 80 }}>
                                        <b>Status:</b>
                                      </p>
                                      <p>
                                        {record.extraData?.isActive ? (
                                          <span style={{ color: token.colorSuccess }}>Active</span>
                                        ) : (
                                          <span style={{ color: token.colorError }}>Suspended</span>
                                        )}
                                      </p>
                                    </Space>

                                    {record.extraData?.role?.type !== 'patient' && (
                                      <Space>
                                        <p style={{ width: 80 }}>
                                          <b>Manager:</b>
                                        </p>
                                        <p>{record.extraData?.role?.isManager ? 'Yes' : 'No'}</p>
                                      </Space>
                                    )}

                                    <Space>
                                      <p style={{ width: 80 }}>
                                        <b>Address:</b>
                                      </p>
                                      <p>{record.residentialAddress}</p>
                                    </Space>
                                    <Space>
                                      <p style={{ width: 80 }}>
                                        <b>DOB:</b>
                                      </p>
                                      <p>{dayjs(record.dateOfBirth).format('DD/MM/YYYY')}</p>
                                    </Space>
                                  </Space>
                                </div>
                              </Col>
                              <Col span={12}>
                                {record.extraData?.role?.type !== 'patient' ? (
                                  <div>
                                    <Space direction='vertical' style={{ width: '100%' }}>
                                      {record.extraData.role.type === 'medical_professional' ? (
                                        <>
                                          <Space>
                                            <p style={{ width: 110 }}>
                                              <b>Profession:</b>
                                            </p>
                                            <p>
                                              {record.extraData?.profession
                                                ? record.extraData.profession.charAt(0).toUpperCase() +
                                                  record.extraData.profession.slice(1)
                                                : 'N/A'}
                                            </p>
                                          </Space>
                                          <Space>
                                            <p style={{ width: 110 }}>
                                              <b>Services</b>
                                            </p>
                                            <p>{getServices(record.extraData?.services)}</p>
                                          </Space>
                                        </>
                                      ) : (
                                        <Space>
                                          <p style={{ width: 110 }}>
                                            <b>Role</b>
                                          </p>
                                          <p>
                                            {record.extraData.role.type.charAt(0).toUpperCase() +
                                              record.extraData.role.type.slice(1)}
                                          </p>
                                        </Space>
                                      )}
                                      <Space>
                                        <p style={{ width: 110 }}>
                                          <b>Clinics:</b>
                                        </p>
                                        <p>
                                          {record.extraData?.clinics ? getClinics(record.extraData?.clinics) : 'N/A'}
                                        </p>
                                      </Space>
                                    </Space>
                                  </div>
                                ) : undefined}
                                {record.extraData?.role?.type === 'patient' && (
                                  <div>
                                    <Space direction='vertical' style={{ width: '100%' }}>
                                      <Space>
                                        <p style={{ width: 110 }}>
                                          <b>Main Member:</b>
                                        </p>
                                        <p>{record.mainMemberId ? record.mainMemberName : 'N/A'}</p>
                                      </Space>
                                      <Space>
                                        <p style={{ width: 110 }}>
                                          <b>Medical Aid:</b>
                                        </p>
                                        <p>{record.medicalAid?.name ? record.medicalAid.name : 'N/A'}</p>
                                      </Space>
                                      <Space>
                                        <p style={{ width: 110 }}>
                                          <b>Plan:</b>
                                        </p>
                                        <p>{record.medicalAid?.plan ? record.medicalAid.plan : 'N/A'}</p>
                                      </Space>
                                      <Space>
                                        <p style={{ width: 110 }}>
                                          <b>Option:</b>
                                        </p>
                                        <p>{record.medicalAid?.planOption ? record.medicalAid.planOption : 'N/A'}</p>
                                      </Space>

                                      <Space>
                                        <p style={{ width: 110 }}>
                                          <b>Dep Nr.:</b>
                                        </p>
                                        <p>
                                          {record.medicalAid?.dependantNumber
                                            ? record.medicalAid.dependantNumber
                                            : 'N/A'}
                                        </p>
                                      </Space>
                                    </Space>
                                  </div>
                                )}
                              </Col>
                            </Row>
                          )
                        }
                      }
                }
                locale={{
                  emptyText: loading ? (
                    <div style={{ padding: '20px 0' }}>
                      <Skeleton active paragraph={{ rows: 3 }} />
                    </div>
                  ) : (
                    <Empty description={`There are currently no registered ${viewType} to show.`} />
                  )
                }}
                scroll={{ x: 1000 }}
                bordered
              />
            </Col>
          </Row>
        </div>
      </ContainerCard>
      <TeamChatForm modalProps={teamChatProps} setModalProps={setTeamChatProps} />
      {/* Begin Examination Modal - Only for medical professionals */}
      {userIsMedicalProfessional && selectedRecord && (
        <BeginExaminationModal
          examinationModalOpen={examinationModalOpen}
          setExaminationModalOpen={setExaminationModalOpen}
          beginAdhocExamination={(data) => beginAdhocScreening(data, selectedRecord)}
        />
      )}
    </>
  )
}

SystemUsersTable.propTypes = {
  addPatient: PropTypes.func.isRequired,
  createRecord: PropTypes.func.isRequired,
  editRecord: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  type: PropTypes.string.isRequired,
  handleSearch: PropTypes.func.isRequired,
  clinics: PropTypes.array.isRequired,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  refreshView: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setPageSize: PropTypes.func.isRequired,
  total: PropTypes.number.isRequired
}

export default memo(SystemUsersTable)
